import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useData } from "../contexts/DataContext";
import { Header } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { fmtPhone } from "../lib/time";
import SourceBadge from "../components/SourceBadge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Search, Phone, Mail, MapPin, Loader2, Trash2 } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { ContactListSkeleton } from "../components/skeletons";

export default function Contacts() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { 
    contacts, 
    fetchContacts, 
    createContact, 
    deleteContact,
    dropdownOptions,
    fetchDropdownOptions,
    loading 
  } = useData();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [filterService, setFilterService] = useState("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    notes: "",
    tags: [],
    lead_source: "",
    service_type: "",
    classification: "retail",
    referral_name: "",
    sms_consent: false
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchContacts();
    fetchDropdownOptions();
  }, [fetchContacts, fetchDropdownOptions]);

  useEffect(() => {
    if (searchParams.get("create") === "true") {
      setShowCreateModal(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const serviceTypes = dropdownOptions.service_types || [];
  const leadSources = dropdownOptions.lead_sources || [];
  const classifications = dropdownOptions.classifications || [];
  const tags = dropdownOptions.tags || [];

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = 
      contact.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.phone?.includes(searchQuery);
    
    const matchesService = filterService === "all" || contact.service_type === filterService;
    
    return matchesSearch && matchesService;
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error("Contact name is required"); return; }
    if (!formData.address.trim()) { toast.error("Address is required"); return; }
    setSubmitting(true);
    try {
      await createContact(formData);
      toast.success("Contact created successfully!");
      // Reset form and close modal
      setFormData({
        name: "",
        phone: "",
        email: "",
        address: "",
        city: "",
        state: "",
        zip: "",
        notes: "",
        tags: [],
        lead_source: "",
        service_type: "",
        classification: "retail",
        referral_name: "",
        sms_consent: false
      });
      setSubmitting(false);
      setShowCreateModal(false);
      return;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create contact");
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (window.confirm("Move this contact to trash?")) {
      try {
        await deleteContact(id);
        toast.success("Moved to trash");
      } catch (error) {
        toast.error("Failed to delete contact");
      }
    }
  };

  const getServiceColor = (serviceType) => {
    const service = serviceTypes.find(s => s.value === serviceType);
    return service?.color || "#6B7280";
  };

  return (
    <div className="min-h-screen">
      <Header title="Contacts">
        <Button
          className="bg-[#0F4C81] hover:bg-[#0D4270] h-12 px-6"
          onClick={() => setShowCreateModal(true)}
          data-testid="create-contact-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Contact
        </Button>
      </Header>

      <div className="p-4 sm:p-6">
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12"
              data-testid="search-contacts"
            />
          </div>
          <Select value={filterService} onValueChange={setFilterService}>
            <SelectTrigger className="w-full md:w-48 h-12" data-testid="filter-service">
              <SelectValue placeholder="All Services" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Services</SelectItem>
              {serviceTypes.map(service => (
                <SelectItem key={service.value} value={service.value}>
                  {service.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Contacts List */}
        {loading ? (
          <ContactListSkeleton rows={6} />
        ) : filteredContacts.length > 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {filteredContacts.map((contact) => (
              <div
                key={contact.id}
                className="flex items-center gap-4 px-4 py-2.5 hover:bg-slate-50 cursor-pointer group transition-colors"
                onClick={() => navigate(`/contacts/${contact.id}`, { state: { from: "/contacts" } })}
                data-testid={`contact-card-${contact.id}`}
              >
                <div className="w-8 h-8 rounded-full bg-[#0F4C81]/10 text-[#0F4C81] flex items-center justify-center text-sm font-semibold flex-shrink-0">
                  {contact.name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0 flex items-center gap-6">
                  <div className="w-44 flex-shrink-0 flex items-center gap-2 min-w-0">
                    <span className="font-medium text-slate-800 truncate">{contact.name}</span>
                    <SourceBadge source={contact.lead_source} size="xs" />
                  </div>
                  {contact.phone && (
                    <span className="text-sm text-slate-500 flex items-center gap-1.5 w-36 flex-shrink-0 hidden sm:flex">
                      <Phone className="w-3 h-3 text-slate-400" />{contact.phone}
                    </span>
                  )}
                  {contact.email && (
                    <span className="text-sm text-slate-500 flex items-center gap-1.5 flex-1 min-w-0 truncate hidden md:flex">
                      <Mail className="w-3 h-3 text-slate-400 flex-shrink-0" />{contact.email}
                    </span>
                  )}
                  {contact.address && (
                    <span className="text-sm text-slate-400 flex items-center gap-1.5 w-48 truncate flex-shrink-0 hidden lg:flex">
                      <MapPin className="w-3 h-3 flex-shrink-0" />{contact.address}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {contact.service_type && (
                    <Badge
                      className="text-xs hidden sm:inline-flex"
                      style={{
                        backgroundColor: `${getServiceColor(contact.service_type)}20`,
                        color: getServiceColor(contact.service_type)
                      }}
                    >
                      {serviceTypes.find(s => s.value === contact.service_type)?.label || contact.service_type}
                    </Badge>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-400 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity h-7 w-7 p-0"
                    onClick={(e) => handleDelete(contact.id, e)}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-700 mb-2">
              {searchQuery || filterService !== "all" ? "No contacts found" : "No contacts yet"}
            </h3>
            <p className="text-slate-500 mb-4">
              {searchQuery || filterService !== "all" 
                ? "Try adjusting your search or filters" 
                : "Add your first contact to get started"}
            </p>
            {!searchQuery && filterService === "all" && (
              <Button
                className="bg-[#0F4C81] hover:bg-[#0D4270]"
                onClick={() => setShowCreateModal(true)}
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Contact
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Create Contact Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Contact</DialogTitle>
            <DialogDescription>
              Create a new contact in your CRM
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreate} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Smith"
                required
                className="h-12"
                data-testid="contact-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: fmtPhone(e.target.value) })}
                  placeholder="(555) 123-4567"
                  className="h-12"
                  data-testid="contact-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  className="h-12"
                  data-testid="contact-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Street Address *</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main Street"
                required
                className="h-12"
                data-testid="contact-address-input"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Charleston"
                  className="h-12"
                  data-testid="contact-city-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="SC"
                  className="h-12"
                  data-testid="contact-state-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip">Zip</Label>
                <Input
                  id="zip"
                  value={formData.zip}
                  onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                  placeholder="29401"
                  className="h-12"
                  data-testid="contact-zip-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Lead Source</Label>
                <Select 
                  value={formData.lead_source} 
                  onValueChange={(v) => setFormData({ ...formData, lead_source: v })}
                >
                  <SelectTrigger className="h-12" data-testid="contact-source-select">
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    {leadSources.map(source => (
                      <SelectItem key={source.value} value={source.value}>
                        {source.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Classification</Label>
                <Select 
                  value={formData.classification} 
                  onValueChange={(v) => setFormData({ ...formData, classification: v })}
                >
                  <SelectTrigger className="h-12" data-testid="contact-classification-select">
                    <SelectValue placeholder="Select classification" />
                  </SelectTrigger>
                  <SelectContent>
                    {classifications.map(c => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Referral Name</Label>
              <Input
                value={formData.referral_name}
                onChange={(e) => setFormData({ ...formData, referral_name: e.target.value })}
                placeholder="Who referred this contact?"
                className="h-12"
                data-testid="contact-referral-name"
              />
            </div>

            <div
              className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 ${formData.sms_consent ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}
              data-testid="create-contact-sms-consent"
            >
              <div>
                <p className="text-sm font-medium text-slate-700">OK to text</p>
                <p className="text-[11px] text-slate-500">Customer agreed to texts — sends the welcome SMS right away</p>
              </div>
              <Switch
                checked={!!formData.sms_consent}
                onCheckedChange={(v) => setFormData({ ...formData, sms_consent: v })}
                disabled={!formData.phone}
                className="data-[state=checked]:bg-emerald-600"
                data-testid="create-contact-sms-consent-switch"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={3}
                data-testid="contact-notes-input"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1 h-12"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 h-12 bg-[#0F4C81] hover:bg-[#0D4270]"
                disabled={submitting}
                data-testid="submit-contact-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Contact"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
